import queryString from 'query-string';
import { useCallback } from 'react';

import { API_CUSTOM_SEGMENTS_RESOLVE_EXTERNAL_URL } from '../constants/apiUrls';
import { getRequestJSON } from '../util/services';
import { useConfig } from './useConfig';

// Shape returned by the backend resolve_external action, which carries the
// resolved ventura segment. The backend aliases segment_id to id, so either is
// present on a match.
export interface ResolvedSegment {
  id?: string | number;
  segment_id?: string | number;
  name?: string;
  [key: string]: unknown;
}

// external_source values the backend resolve_external endpoint accepts. Note
// these are not identical to the AI-search response group keys: the `segments`
// group resolves under external_source `inmarket`. Both external_source and
// external_id must be sent together.
export type ExternalSource = 'esri' | 'inmarket' | 'demographic' | 'liveramp';

// Outcome of a resolve attempt. Distinguished so the caller can tell a
// legitimate miss ('not_found' - the AI result has no backfilled ventura
// segment) from an unexpected server/network failure ('error'), which deserve
// different messaging.
export type ResolveResult =
  | { status: 'found'; segment: ResolvedSegment }
  | { status: 'not_found' }
  | { status: 'error' };

// Resolve an AI-audience search result to its backfilled ventura segment via the
// (external_source, external_id) pair, so the caller never attaches a segment
// with no id.
export default function useResolveExternalSegment() {
  const { apiHost } = useConfig();

  return useCallback(
    async (externalSource: ExternalSource, externalId: string): Promise<ResolveResult> => {
      try {
        const result = await getRequestJSON(
          apiHost,
          `${API_CUSTOM_SEGMENTS_RESOLVE_EXTERNAL_URL}?${queryString.stringify({
            external_source: externalSource,
            external_id: externalId,
          })}`
        );
        // A 2xx with an empty/blank body, or without a segment id, means no
        // match rather than a resolved segment.
        if (!result || (result.segment_id == null && result.id == null)) {
          return { status: 'not_found' };
        }
        return { status: 'found', segment: result };
      } catch (error) {
        // fetchHelper rejects on any non-2xx with an Error whose message is the
        // response body. The backend 404 body is a JSON error about "No segment
        // found ..."; anything else (500, timeout, network) is an unexpected
        // failure. Only the former is a legitimate miss.
        const message = error instanceof Error ? error.message : '';
        if (message.includes('No segment found')) {
          return { status: 'not_found' };
        }
        return { status: 'error' };
      }
    },
    [apiHost]
  );
}
